/* eslint-disable class-methods-use-this */

import type { ColDef, ValueGetterParams, ValueParserParams } from "@ag-grid-community/core";
import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { EMPTY_STRING, noop } from "@regrello/core-utils";
import {
  type FieldInstanceFields,
  FormConstraintConditionOperator,
  type FormFieldFields,
  PropertyDataType,
  type PropertyTypeFields,
  type SpectrumFieldValidationTypeFields,
  type SpectrumFieldVersionFields,
  type SpectrumValueConstraintFields,
} from "@regrello/graphql-api";
import type { ReactNode } from "react";
import type { FieldArrayWithId, UseFormReturn } from "react-hook-form";

import type { ConfigureSpectrumFieldFormFormFields } from "../../../../views/modals/formDialogs/spectrumFields/_internal/ConfigureSpectrumFieldForm";
import type { CustomFieldPlugin } from "../../../customFields/plugins/types/CustomFieldPlugin";
import { RegrelloFormFieldNumber } from "../../../formFields/RegrelloFormFieldNumber";
import {
  type ProspectiveSpectrumFieldVersion,
  SpectrumFieldPluginDecorator,
} from "../../types/SpectrumFieldPluginDecorator";
import { SpectrumFieldValidationType } from "../../types/SpectrumFieldValidationType";
import { FrontendValueConstraintRuleName } from "../../utils/spectrumFieldConstraintUtils";
import { RegrelloEditableCellCurrency } from "./_internal/RegrelloEditableCellCurrency";
import { SpectrumCurrencyValueConstraints } from "./_internal/SpectrumCurrencyValueConstraints";
import type { CurrencyFieldPluginFrontendValue } from "./_internal/types";

export class SpectrumCurrencyFieldPluginDecorator<
  T extends CurrencyFieldPluginFrontendValue,
> extends SpectrumFieldPluginDecorator<T> {
  constructor(plugin: CustomFieldPlugin<T>) {
    super(plugin);
    this.uri = "com.regrello.spectrumField.currency";
  }

  public canProcessValidationType(spectrumValidationType: ProspectiveSpectrumFieldVersion["validationType"]) {
    return spectrumValidationType.validationType === SpectrumFieldValidationType.CURRENCY;
  }

  public canProcessSpectrumField(field: ProspectiveSpectrumFieldVersion) {
    return (
      this.canProcessPropertyDataType(field.propertyType.dataType) &&
      this.canProcessValidationType(field.validationType)
    );
  }

  public findPropertyTypeFromLoadedPropertyTypes(propertyTypes: PropertyTypeFields[]) {
    return propertyTypes.find((propertyType) => propertyType.dataType === PropertyDataType.FLOAT);
  }

  public findValidationTypeFromLoadedValidationTypes(validationTypes: SpectrumFieldValidationTypeFields[]) {
    return validationTypes.find(
      (validationType) => validationType.validationType === SpectrumFieldValidationType.CURRENCY,
    );
  }

  public findValueConstraintsFromLoadedValueConstraints(valueConstraints: SpectrumValueConstraintFields[]) {
    return valueConstraints
      .filter(
        ({ valueConstraintRule }) =>
          valueConstraintRule === FrontendValueConstraintRuleName.MAX_FLOAT ||
          valueConstraintRule === FrontendValueConstraintRuleName.MIN_FLOAT ||
          valueConstraintRule === FrontendValueConstraintRuleName.DECIMAL_PLACES ||
          valueConstraintRule === FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT,
      )
      .sort((a, b) => {
        // Sort order: DECIMAL_PLACES, MIN_INT, MAX_INT
        const order: Partial<Record<FrontendValueConstraintRuleName, number>> = {
          [FrontendValueConstraintRuleName.DECIMAL_PLACES]: 1,
          [FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT]: 2,
          [FrontendValueConstraintRuleName.MIN_FLOAT]: 3,
          [FrontendValueConstraintRuleName.MAX_FLOAT]: 4,
        };

        const aOrder = order[a.valueConstraintRule as FrontendValueConstraintRuleName] ?? 5;
        const bOrder = order[b.valueConstraintRule as FrontendValueConstraintRuleName] ?? 5;

        return aOrder - bOrder;
      });
  }

  public getConstraintConditionOperators() {
    return {
      [FormConstraintConditionOperator.IS_ZERO]: {
        label: t`Is empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.IS_NOT_ZERO]: {
        label: t`Is not empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.GT]: {
        label: t`Is greater than`,
        inputCount: 1,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.GTE]: {
        label: t`Is greater than or equal to`,
        inputCount: 1,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.LT]: {
        label: t`Is less than`,
        inputCount: 1,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.LTE]: {
        label: t`Is less than or equal to`,
        inputCount: 1,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.EQ]: {
        label: t`Is equal to`,
        inputCount: 1,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.NOT]: {
        label: t`Is not equal to`,
        inputCount: 1,
        isMultiselect: false,
      },
    };
  }

  public getSpectrumFormAutosaveMode() {
    return "onBlur" as const;
  }

  public renderPreviewFormField(spectrumField: SpectrumFieldVersionFields) {
    return (
      <RegrelloFormFieldNumber
        currencySymbol={spectrumField.fieldUnit?.symbol || "$"}
        disabled={true}
        onChange={noop}
        size="medium"
        value={Number.NaN}
      />
    );
  }

  public renderValueConstraints(props: {
    constraints: Array<FieldArrayWithId<ConfigureSpectrumFieldFormFormFields, "valueConstraints">>;
    disabled: boolean;
    form: UseFormReturn<ConfigureSpectrumFieldFormFormFields>;
  }): ReactNode {
    return <SpectrumCurrencyValueConstraints {...props} />;
  }

  public getColumnDef<RowDatum, T>(
    fieldInstance: FieldInstanceFields,
    _formField: FormFieldFields,
    colDefParams: Partial<ColDef<RowDatum, T>>,
  ): ColDef<RowDatum, T> {
    const spectrumFieldVersion = fieldInstance.spectrumFieldVersion;
    if (spectrumFieldVersion == null) {
      throw new Error("SpectrumFieldVersion is null on fieldInstance, this should never happen");
    }

    return {
      ...colDefParams,
      type: "numericColumn",
      cellClass: "regrello-center-aligned-cell",
      cellEditor: RegrelloEditableCellCurrency,
      cellEditorParams: {
        fieldInstance,
      },
      cellEditorPopup: true,
      cellEditorPopupPosition: "over", // eslint-disable-line lingui/no-unlocalized-strings
      cellRenderer: (params: ColDef["cellRendererParams"]) => {
        return (
          <div className="h-full flex items-center gap-1">
            {params.value != null ? (
              <>
                <span>{spectrumFieldVersion.fieldUnit?.symbol}</span>
                <span>{i18n.number(params.value)}</span>
              </>
            ) : (
              <span>{EMPTY_STRING}</span>
            )}
          </div>
        );
      },
      headerClass: "regrello-left-aligned-header",
      valueGetter: (params: ValueGetterParams<RowDatum, T>) => {
        const value =
          params.data != null &&
          params.colDef.colId != null &&
          // biome-ignore lint/suspicious/noExplicitAny: <explanation>
          (params.data as any)?.[params.colDef.colId];
        return parseValueFromCell(value) as T;
      },
      valueParser: (params: ValueParserParams<RowDatum, T>) => {
        // (clewis): Cast because Even though T extends string, TypeScript thinks it could be a more
        // specific subtype of string (like a string literal type or a union of strings).
        return parseValueFromCell(params.newValue) as T;
      },
    };
  }
}

function parseValueFromCell(value: string | number): CurrencyFieldPluginFrontendValue {
  const resolvedValue = typeof value === "string" ? Number.parseFloat(value) : value;
  if (Number.isFinite(resolvedValue)) {
    return resolvedValue;
  }
  return Number.NaN;
}
